import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MultiSelectAsyncV2 from '../MultiSelectAsyncV2';
import BaseService from 'services/BaseService';
import { buildLikeOption, isLikeOption, resolveLikeSelection } from '../nsirFilterLikeOption';

const appendCsvParam = (params, key, values) => {
  if (!values || !Array.isArray(values) || values.length === 0) return;
  params.append(key, values.join(','));
};

const NsirSelectV2 = ({
  onSelect,
  value,
  isRequired,
  error,
  helperText,
  disabled,
  label = 'Nsirs',
  tipoServicos,
  subTipoIds,
  minChars = 2,
  like = false,
  likeValue = undefined,
  likeNoun,
}) => {
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const noun = likeNoun || label;

  useEffect(() => {
    let cancelled = false;
    const hasIds = value && Array.isArray(value) && value.length > 0 && typeof value[0] !== 'object';

    if (hasIds) {
      setLoadingInitial(true);
      const hydrateValues = async () => {
        try {
          const params = new URLSearchParams();
          params.append('nsirIds', value.join(','));
          const response = await BaseService.get({ url: `nsirs/options/ids?${params.toString()}` });
          const formatted = (response.data || []).map(o => ({ value: o.id, label: o.label }));
          if (!cancelled) setSelectedObjects(formatted);
        } catch (e) {
          console.error('Erro ao hidratar valores iniciais:', e);
        } finally {
          if (!cancelled) setLoadingInitial(false);
        }
      };
      hydrateValues();
      return () => {
        cancelled = true;
      };
    }

    if (like && likeValue) {
      setSelectedObjects([buildLikeOption(likeValue, noun)]);
      return undefined;
    }

    setSelectedObjects([]);
    return undefined;
  }, [value, like, likeValue, noun]);

  const loadOptions = async (inputValue) => {
    const trimmed = (inputValue || '').trim();
    const likeOption = like && trimmed.length >= minChars
      ? [buildLikeOption(trimmed, noun)]
      : [];
    if (!inputValue || inputValue.length < minChars) return [];
    try {
      const params = new URLSearchParams();
      params.append('search', inputValue);
      appendCsvParam(params, 'tipoServicos', tipoServicos);
      appendCsvParam(params, 'subTipoIds', subTipoIds);
      const response = await BaseService.get({ url: `nsirs/options?${params.toString()}` });
      const options = (response.data || []).map(o => ({ value: o.id, label: o.label }));
      return [...likeOption, ...options];
    } catch (e) {
      console.error('Erro na busca assíncrona:', e);
      return likeOption;
    }
  };

  return (
    <MultiSelectAsyncV2
      label={label}
      error={error}
      helperText={helperText}
      isRequired={isRequired}
      disabled={disabled || loadingInitial}
      loadOptions={loadOptions}
      value={selectedObjects}
      placeholder={
        loadingInitial
          ? 'Carregando...'
          : `Digite ${minChars}+ caracteres para buscar`
      }
      onChange={(selected) => {
        if (!like) {
          const next = selected || [];
          setSelectedObjects(next);
          const ids = next.length ? next.map(v => v.value) : null;
          const labels = next.length ? next.map(v => v.label) : null;
          onSelect(ids, labels);
          return;
        }
        const next = resolveLikeSelection(selectedObjects, selected);
        setSelectedObjects(next);
        const likeOpt = next.find(isLikeOption);
        if (likeOpt) {
          onSelect(null, null, likeOpt.likeQuery);
          return;
        }
        const ids = next.length ? next.map(v => v.value) : null;
        const labels = next.length ? next.map(v => v.label) : null;
        onSelect(ids, labels, null);
      }}
    />
  );
};

NsirSelectV2.propTypes = {
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.array,
  isRequired: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  tipoServicos: PropTypes.array,
  subTipoIds: PropTypes.array,
  minChars: PropTypes.number,
  like: PropTypes.bool,
  likeValue: PropTypes.string,
  likeNoun: PropTypes.string,
};

NsirSelectV2.defaultProps = {
  value: null,
  isRequired: false,
  error: false,
  helperText: undefined,
  disabled: false,
  label: 'Nsirs',
  tipoServicos: undefined,
  subTipoIds: undefined,
  minChars: 2,
  like: false,
  likeValue: undefined,
  likeNoun: undefined,
};

export default NsirSelectV2;
